/**
 * Is he away from an event's city, for the whole of when it happens?
 *
 * Doug's note, 21 September: "Don't show me events for dates that I'm out of
 * town." A stay of kind `'home2'` or `'travel'` is time away from home.
 * The second home is its own kind, not a label on a travel row — the year
 * calendar treats it that way — and it is the place, not the kind, that decides
 * whether an event is affected:
 *
 *   - an event in the city he is visiting is not hidden by being away from
 *     Berlin, because he is not away from it;
 *   - a run that only touches the edge of a trip — a two-week exhibition
 *     with one evening inside a three-day trip — is not hidden either,
 *     because every other day of it is still a day he could go. Only a run
 *     that is entirely inside the trip has nowhere left to land.
 *
 * So the question is containment, not overlap: is the whole of [from, to]
 * inside a single trip, inclusive of both ends, to somewhere that is neither
 * home nor the event's own city. One job — "is he away" — and nothing about
 * whether that makes a good recommendation, which is a different question
 * `filmRank` already owns for a different reason.
 *
 * It imports `reach.ts`, with the extension. It used to import nothing,
 * because an extensionless import fails the Worker's suite (which loads this
 * file directly) and an import with one used to fail `tsc` here; since
 * `app/tsconfig.json` sets `allowImportingTsExtensions` the second half no
 * longer holds, and retyping `HOME_CITY` here was the second copy of a
 * constant that C2.1 replaced with regions. `reach.ts` is import-free itself.
 */
import { regionOf, type ReachRegion, type ReachTown } from './reach.ts'

/** Trap T2: a row with no city is a Berlin row until C1.x writes a city on
 *  every row — `reachOf`'s own rule, and `zone.ts`'s fallback. */
const NULL_CITY_REGION = 'berlin'

/** The shape `stays` needs to have. Duck-typed rather than imported from
 *  `types.ts`, for the same reason `inReach` in `entries.ts` takes the same
 *  shape rather than the `Stay` interface: this file has nothing else to do
 *  with the rest of the app and should not need to resolve it. `region_id`
 *  is C0.2's column; absent on every row until it is applied. */
export interface TravelStay {
  kind: string
  label: string | null
  starts_on: string
  ends_on: string
  region_id?: string | null
}

/** Where home is and which towns belong to which region: the person's home
 *  region (`profiles.home_region`, the Year's `'Home'`) and `region_towns`. */
export interface AwayWhere {
  home: ReachRegion & { name: string }
  towns: readonly ReachTown[]
}

/**
 * Is he away from `city`, for every day of `from`..`to` inclusive?
 *
 * `city` null is a Berlin row (T2), the same call `reachOf` makes: every
 * source here is a Berlin source until C1.x, and a row with no city on it is
 * not a reason to hide anything while he is home.
 */
export function isAway(
  city: string | null, from: string, to: string, stays: TravelStay[],
  where: AwayWhere,
  /** The profile's second-home label ('Ireland'), the place a `home2` stay is
   *  whatever its own label says. Null names it nothing, so a `home2` stay
   *  hides home events and never matches an event's city. */
  secondHomeLabel?: string | null,
): boolean {
  const eventCity = (city ?? '').trim().toLowerCase()
  const eventRegion = eventCity ? regionOf(city, where.towns) : NULL_CITY_REGION
  const homeName = where.home.name.trim().toLowerCase()
  return stays.some(s => {
    // A visit is someone coming here. He is not away.
    if (s.kind !== 'travel' && s.kind !== 'home2') return false
    // The second home is the place, whatever the row's label says. A travel
    // row's place is its label, and a blank one is not a trip anywhere.
    const place = s.kind === 'home2' ? (secondHomeLabel ?? 'home2').trim().toLowerCase() : (s.label ?? '').trim().toLowerCase()
    if (!place || place === homeName) return false
    // Visiting that very city: the event is where he is, not where he isn't.
    if (eventCity && place === eventCity) return false
    // Staying in that region: a Concord stay (`region_id` boston) does not
    // hide a Cambridge concert. A stay in the home region's own towns — a
    // weekend in Dresden — still hides home events, as it always did.
    if (s.region_id && s.region_id !== where.home.id && s.region_id === eventRegion) return false
    // Inclusive of both ends, and the whole window has to sit inside the
    // trip — a trip that only touches one end of a longer run does not
    // contain it.
    return s.starts_on <= from && to <= s.ends_on
  })
}

/** The convenience most screens actually want: a row's own dates, read the
 *  same way `sortDate` and `alreadyHappened` in `entries.ts` read them.
 *  An undated row has no window to be away for, so it is never hidden by
 *  this — the same treatment `isNear` gives an undated row. */
export function awayFromEvent(
  e: { city?: string | null; run_start?: string | null; run_end?: string | null; published_on?: string | null },
  trips: TravelStay[],
  where: AwayWhere,
  secondHomeLabel?: string | null,
): boolean {
  const from = e.run_start ?? e.published_on ?? null
  if (!from) return false
  const to = e.run_end ?? from
  return isAway(e.city ?? null, from, to, trips, where, secondHomeLabel)
}
